import React, { useState, useEffect, useReducer, useContext } from "react";
import { useHistory, useLocation } from "react-router-dom";
import qs from "query-string";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import SubscriptionModal from "../../components/SubscriptionModal";
import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import EmptyState from "../../components/EmptyState";
import { StatusBadge } from "../../components/ui";
import { usePanelStyles, Callout } from "../../components/QueueIntegrationModal/panelKit";
import ReceiptOutlinedIcon from "@material-ui/icons/ReceiptOutlined";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { canManageTenants } from "../../utils/tenant";
import { i18n } from "../../translate/i18n";

import moment from "moment";

const reducer = (state, action) => {
  if (action.type === "LOAD_INVOICES") {
    const invoices = action.payload.invoices || action.payload;
    const newInvoices = [];

    invoices.forEach((invoice) => {
      const invoiceIndex = state.findIndex((i) => i.id === invoice.id);
      if (invoiceIndex !== -1) {
        state[invoiceIndex] = invoice;
      } else {
        newInvoices.push(invoice);
      }
    });

    return [...state, ...newInvoices];
  }

  if (action.type === "UPDATE_INVOICES") {
    const invoice = action.payload;
    const invoiceIndex = state.findIndex((i) => i.id === invoice.id);

    if (invoiceIndex !== -1) {
      state[invoiceIndex] = invoice;
      return [...state];
    } else {
      return [invoice, ...state];
    }
  }

  if (action.type === "DELETE_INVOICE") {
    const invoiceId = action.payload;

    const invoiceIndex = state.findIndex((i) => i.id === invoiceId);
    if (invoiceIndex !== -1) {
      state.splice(invoiceIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles(() => ({
  notice: { marginBottom: 14 },
  emptyCell: { padding: 0, borderBottom: "none" },
}));

// rowStatus devolve o rótulo em português; aqui só o tom do selo.
const STATUS_TONE = { Pago: "success", Vencido: "error" };

const Invoices = () => {
  const classes = useStyles();
  const panel = usePanelStyles();
  const { user } = useContext(AuthContext);
  const history = useHistory();
  const location = useLocation();

  // Revenda: `?companyId=` → faturas de uma empresa gerenciada (super / admin de revendedor)
  const companyIdParam = (() => {
    const raw = qs.parse(location.search).companyId;
    const n = parseInt(Array.isArray(raw) ? raw[0] : raw, 10);
    return Number.isInteger(n) && n > 0 ? n : null;
  })();
  const managedCompanyId =
    companyIdParam && canManageTenants(user) && companyIdParam !== user?.companyId
      ? companyIdParam
      : null;
  const [managedCompany, setManagedCompany] = useState(null);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchParam, ] = useState("");
  const [invoices, dispatch] = useReducer(reducer, []);
  const [storagePlans, setStoragePlans] = React.useState([]);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [isCompanyExpired, setIsCompanyExpired] = useState(false);

  // Verificar se a empresa está vencida
  useEffect(() => {
    if (user && user.company) {
      const hoje = moment();
      const vencimento = moment(user.company.dueDate);
      const isExpired = hoje.isAfter(vencimento);
      setIsCompanyExpired(isExpired);
    }
  }, [user]);

  const handleOpenContactModal = (invoices) => {
    setStoragePlans(invoices);
    setSelectedContactId(null);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(false);
  };
  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam, managedCompanyId]);

  useEffect(() => {
    let active = true;
    setManagedCompany(null);
    if (!managedCompanyId) return undefined;
    api
      .get(`/companies/${managedCompanyId}`)
      .then(({ data }) => {
        if (active) setManagedCompany(data);
      })
      .catch((err) => toastError(err));
    return () => {
      active = false;
    };
  }, [managedCompanyId]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchInvoices = async () => {
        try {
          console.log("Buscando faturas...", { searchParam, pageNumber });
          const params = { searchParam, pageNumber };
          if (managedCompanyId) params.companyId = managedCompanyId;
          const { data } = await api.get("/invoices/all", { params });

          console.log("Dados recebidos:", data);
          dispatch({ type: "LOAD_INVOICES", payload: data });
          console.log("Dispatch realizado com payload:", data);
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          console.error("Erro ao buscar faturas:", err);
          toastError(err);
          setLoading(false);
        }
      };
      fetchInvoices();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, managedCompanyId]);

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const rowStatus = (record) => {
    const hoje = moment(moment()).format("DD/MM/yyyy");
    const vencimento = moment(record.dueDate).format("DD/MM/yyyy");
    var diff = moment(vencimento, "DD/MM/yyyy").diff(moment(hoje, "DD/MM/yyyy"));
    var dias = moment.duration(diff).asDays();
    const status = record.status;
    if (status === "paid") {
      return "Pago";
    }
    if (dias < 0) {
      return "Vencido";
    } else {
      return "Em Aberto"
    }
  }
  
  const renderUseWhatsapp = (row) => { return row.status === false ? "Não" : "Sim" };
  const renderUseFacebook = (row) => { return row.status === false ? "Não" : "Sim" };
  const renderUseInstagram = (row) => { return row.status === false ? "Não" : "Sim" };
  const renderUseCampaigns = (row) => { return row.status === false ? "Não" : "Sim" };
  const renderUseSchedules = (row) => { return row.status === false ? "Não" : "Sim" };
  const renderUseInternalChat = (row) => { return row.status === false ? "Não" : "Sim" };
  const renderUseExternalApi = (row) => { return row.status === false ? "Não" : "Sim" };

  return (
    <MainContainer maxWidth>
      <SubscriptionModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        aria-labelledby="form-dialog-title"
        Invoice={storagePlans}
        contactId={selectedContactId}

      ></SubscriptionModal>
      <MainHeader>
        <Title>
          {managedCompanyId
            ? `${i18n.t("invoices.table.of")} ${managedCompany?.name || `#${managedCompanyId}`} (${invoices.length})`
            : `${i18n.t("invoices.table.invoices")} (${invoices.length})`}
        </Title>
        {managedCompanyId && (
          <MainHeaderButtonsWrapper>
            <Button variant="outlined" onClick={() => history.push("/companies")}>
              {i18n.t("invoices.table.back")}
            </Button>
          </MainHeaderButtonsWrapper>
        )}
      </MainHeader>
      {!managedCompanyId && isCompanyExpired && (
        <Callout tone="down" icon={<WarningRoundedIcon />} className={classes.notice}>
          <strong>Atenção:</strong> Sua assinatura está vencida. Entre em contato com o suporte para regularizar sua situação.
        </Callout>
      )}
      <Paper
        className={panel.tableCard}
        elevation={0}
        onScroll={handleScroll}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Detalhes</TableCell>
              <TableCell align="right">Usuários</TableCell>
              <TableCell align="right">Conexões</TableCell>
              <TableCell align="right">Filas</TableCell>
              <TableCell align="right">Valor</TableCell>
              <TableCell>Data Venc.</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Ação</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {invoices.map((invoices) => {
                const status = rowStatus(invoices);
                return (
                  <TableRow key={invoices.id}>
                    <TableCell className={panel.cellStrong}>{invoices.detail}</TableCell>
                    <TableCell align="right">{invoices.users}</TableCell>
                    <TableCell align="right">{invoices.connections}</TableCell>
                    <TableCell align="right">{invoices.queues}</TableCell>
                    <TableCell align="right" className={panel.cellStrong}>
                      {invoices.value.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}
                    </TableCell>
                    <TableCell>{moment(invoices.dueDate).format("DD/MM/YYYY")}</TableCell>
                    <TableCell>
                      <StatusBadge color={STATUS_TONE[status] || "warning"} label={status} />
                    </TableCell>
                    <TableCell align="right">
                      {status !== "Pago" ? (
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          onClick={() => handleOpenContactModal(invoices)}
                        >
                          Pagar
                        </Button>
                      ) : (
                        <span className={panel.cellMuted}>—</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {loading && <TableRowSkeleton columns={8} />}
              {!loading && invoices.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className={classes.emptyCell}>
                    <EmptyState
                      icon={<ReceiptOutlinedIcon />}
                      title="Nenhuma fatura por aqui"
                      message="As faturas da assinatura aparecem nesta lista assim que forem geradas."
                    />
                  </TableCell>
                </TableRow>
              )}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Invoices;
